import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureContext } from '@/contexts/FeatureContext';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { permissionForPath } from '@/config/routePermissions';
import { useSubscription } from '@/hooks/useSubscription';
import { AccessCheckError } from '@/components/AccessCheckError';
import { AccountDeactivated } from '@/components/auth/AccountDeactivated';
import { AwaitingSeat } from '@/components/auth/AwaitingSeat';
import { Loader2 } from '@/components/icons';
import { rememberEstimateVisitForSignIn } from '@/features/quotes/lib/estimateDraftVisit';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: Array<'admin' | 'user' | 'crew' | 'super_admin' | 'marketing_agency' | 'affiliate' | 'support_staff'>;
  /**
   * Org-role permission key. Authoritative when present (or when the current path
   * is permission-mapped in routePermissions) — allowedRoles is then ignored so
   * custom roles work. Denied → /forbidden. Fails open on permission-fetch error
   * so a user is never locked out of their own app by an RPC failure.
   */
  requiredPermission?: string;
  requiresSubscription?: boolean; // New prop to control subscription requirement
}

// Paths that don't require an active subscription
const SUBSCRIPTION_EXEMPT_PATHS = [
  '/subscription-management',
  '/checkout/success',
  '/checkout/cancel',
  '/auth',
  '/auth/confirm-email',
  '/auth/reset-password',
  '/auth/complete-profile',
  '/crew/accept-invitation',
  '/forbidden',
  '/debug-crew'
];

export function ProtectedRoute({
  children,
  allowedRoles,
  requiredPermission,
  requiresSubscription = true
}: ProtectedRouteProps) {
  const { user, loading, role, profileStatus, accessScope, refreshProfile } = useAuth();
  const { can, isLoading: permissionsLoading } = usePermissionContext();
  const { subscription, isLoading: subscriptionLoading, isFetching: subscriptionFetching, error: subscriptionError, refetch: retrySubscription } = useSubscription();
  const { isLoading: featuresLoading, error: featuresError, refetch: retryFeatures } = useFeatureContext();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading LightMaster...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    const redirectTo = `${location.pathname}${location.search}`;
    rememberEstimateVisitForSignIn(location);
    return <Navigate to={`/auth?redirect=${encodeURIComponent(redirectTo)}`} replace />;
  }

  if (profileStatus === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (profileStatus === 'deactivated') {
    return <AccountDeactivated />;
  }

  if (profileStatus === 'awaiting_seat') {
    return <AwaitingSeat />;
  }

  if (profileStatus === 'signup_completion_required') {
    return <Navigate to="/auth/complete-profile" replace />;
  }

  if (profileStatus === 'missing_profile_error') {
    return <Navigate to="/auth/complete-profile" replace />;
  }

  if (accessScope === null) {
    return <AccessCheckError retry={refreshProfile} />;
  }

  // Permission is authoritative when the route is mapped (or an explicit prop is
  // given): the admin's per-role permission controls the page. allowedRoles is the
  // fallback only for unmapped platform/utility routes.
  const effectivePermission = requiredPermission ?? permissionForPath(location.pathname);

  // Platform staff aren't org members, so org-role permissions never resolve
  // for them (super_admin passes because has_permission short-circuits; the
  // scoped support_staff role has no such bypass). When a route explicitly
  // allows support_staff, the role gate is authoritative instead.
  const platformRoleAllowed =
    role === 'support_staff' && !!allowedRoles?.includes('support_staff');

  const permissionSpinner = (
    <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-muted-foreground">Loading permissions...</p>
      </div>
    </div>
  );

  if (effectivePermission && !platformRoleAllowed) {
    // Wait for the initial fetch; on error the context fails open (can() returns
    // true) so we never lock a user out of their own app.
    if (permissionsLoading) {
      return permissionSpinner;
    }
    if (!can(effectivePermission)) {
      return <Navigate to="/forbidden" replace />;
    }
  } else if (allowedRoles && allowedRoles.length > 0) {
    // Wait for role to resolve to avoid false forbidden on first load
    if (!role) {
      return permissionSpinner;
    }
    if (!allowedRoles.includes(role)) {
      return <Navigate to="/forbidden" replace />;
    }
  }

  // Check subscription requirement
  const isExemptPath = SUBSCRIPTION_EXEMPT_PATHS.some(path =>
    location.pathname === path || location.pathname.startsWith(path)
  );

  const isPlatformRole = ['super_admin', 'marketing_agency', 'affiliate', 'support_staff'].includes(role ?? '');
  if (requiresSubscription && !isExemptPath && !isPlatformRole) {
    // Wait for subscription data to load. An empty answer that is being
    // re-checked also waits: it may predate the organization's creation (a
    // signup that just finished), and redirecting on it would send a person
    // who is on their free trial to the paid plan picker.
    if (subscriptionLoading || ((!subscription.planId || subscription.isTrialExpired) && subscriptionFetching)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Checking subscription...</p>
          </div>
        </div>
      );
    }

    if (subscriptionError) {
      return <AccessCheckError retry={() => { void retrySubscription(); }} />;
    }

    // All feature-gated app screens (including the onboarding wizard/sidebar)
    // must distinguish a lookup failure from a genuine plan restriction.
    if (featuresError) return <AccessCheckError retry={retryFeatures} />;
    if (featuresLoading) return permissionSpinner;

    // Two conditions send a tenant to the plan picker:
    //
    //  1. No subscription at all — the pre-existing case (an org created before
    //     the platform trial, or one whose trial grant failed).
    //  2. The 7-day platform trial has lapsed and no choice has been made. This
    //     is the day-7 paywall: they pick a paid plan or explicitly continue on
    //     Free. Their entitlements have already degraded to Free server-side
    //     (resolve_plan_id, migration 20260820005500), so this is a decision
    //     point rather than a lockout — if the redirect ever fails to render,
    //     they get a working Free-tier app, not a broken one.
    //
    // Exception: super_admin + marketing_agency + affiliate + support_staff are
    // platform/agency logins, not paying tenants — they bypass subscription checks.
    if (!subscription.planId || subscription.isTrialExpired) {
      return <Navigate to="/subscription-management" replace />;
    }
  }

  return <>{children}</>;
}
